import { Controller } from "@hotwired/stimulus"
import { isTestEnv } from "../../utils/test_env"

// Drives the "Anna" funnel layout's chat feel on top of the `lead-funnel`
// controller, which still owns the actual funnel logic. As the visitor advances,
// the answered question + their answer slide up into a running history while
// Anna "types", then the next question streams into the frame. Purely additive —
// if anything here throws, the funnel keeps working, you just lose the chrome.
export default class extends Controller {
  static targets = ["history", "frame"]
  // Multiplies every delay below. System specs run the same beats, in the same
  // order, at a hundredth of the wait.
  get pace() {
    return isTestEnv() ? 0.01 : 1
  }

  // Minimum "thinking" time from click to new question, so visitors always see
  // typing dots even when the server replies faster than the staggered intro.
  static MIN_THINKING_MS = 1500

  connect() {
    this.typingEl = null
    this.currentQuestion = this.frameQuestionText()
    this.pendingTimers = []

    this.clickHandler = (event) => this.onClick(event)
    this.element.addEventListener("click", this.clickHandler)

    if (this.hasFrameTarget) {
      // Watch the live frame for the next question streaming in (turbo replaces
      // its contents). A class toggle on the frame itself is not childList, so
      // hiding/showing it never re-triggers this.
      this.observer = new MutationObserver(() => this.onFrameChange())
      this.observer.observe(this.frameTarget, { childList: true, subtree: true })
    }
  }

  disconnect() {
    this.element.removeEventListener("click", this.clickHandler)
    this.observer?.disconnect()
    this.clearPendingTimers()
  }

  schedule(fn, ms) {
    const id = setTimeout(() => {
      this.pendingTimers = this.pendingTimers.filter((t) => t !== id)
      fn()
    }, ms * this.pace)
    this.pendingTimers.push(id)
    return id
  }

  clearPendingTimers() {
    this.pendingTimers.forEach(clearTimeout)
    this.pendingTimers = []
  }

  frameQuestionText() {
    const heading = this.hasFrameTarget ? this.frameTarget.querySelector("h2") : null
    return heading ? heading.textContent.trim() : null
  }

  onClick(event) {
    if (event.target.closest("#backButton")) {
      this.popTurn()
      return
    }

    // Multiple-choice chips advance immediately (no validation) — animate now.
    const option = event.target.closest('[data-lead-funnel-target="optionButton"]')
    if (option) {
      this.advance(this.currentQuestion, option.textContent.trim())
      return
    }

    // Text steps run through the lead-funnel controller's validation first. Defer
    // a tick so that runs, then only animate if it actually started navigating
    // (otherwise a failed validation would leave us stuck on a typing bubble).
    const submit = event.target.closest('[data-lead-funnel-target="submitButton"]')
    if (submit) {
      const question = this.currentQuestion
      const answer = this.submittedAnswer()
      setTimeout(() => {
        if (this.navigationStarted(submit)) this.advance(question, answer)
      }, 0)
    }
  }

  // The lead-funnel controller reveals the button's `.spinner` (hiding
  // `.option-content`) only after validation passes — our signal that the funnel
  // is genuinely advancing and the frame is about to swap.
  navigationStarted(button) {
    const spinner = button.querySelector(".spinner")
    return spinner ? !spinner.classList.contains("hidden") : true
  }

  submittedAnswer() {
    if (!this.hasFrameTarget) return ""
    const phone = this.frameTarget.querySelector('[data-lead-funnel-target="phoneInput"]')
    if (phone) return phone.value.trim()
    const input = this.frameTarget.querySelector('[data-lead-funnel-target="input"]')
    return input ? input.value.trim() : ""
  }

  // Move the answered turn into the history, hide the (now duplicated) live
  // question, and show Anna typing until the next question arrives. Staggered
  // so the conversation unfolds in deliberate beats — and the new question is
  // gated by MIN_THINKING_MS so the typing dots always show *before* it lands,
  // even when the server replies in under that window.
  advance(question, answer) {
    this.clearPendingTimers()
    this.nextQuestion = null
    this.advanceStartedAt = performance.now()

    if (question) this.appendBubble(question, "them")
    this.frameTarget?.classList.add("an-hidden")
    if (answer) this.schedule(() => this.appendBubble(answer, "me"), 300)
    this.schedule(() => this.showTyping(), 500)
  }

  onFrameChange() {
    const next = this.frameQuestionText()
    if (!next) return
    // Same question re-rendered (back nav, validation re-render): unhide the
    // frame and clear typing so the UI doesn't get stuck on the dots.
    if (next === this.currentQuestion) {
      this.hideTyping()
      this.frameTarget?.classList.remove("an-hidden")
      this.scrollToEnd()
      return
    }

    // Hold the new question behind the typing dots until at least
    // MIN_THINKING_MS has passed since the click. If the server was slow, the
    // wait is zero and we reveal immediately.
    this.nextQuestion = next
    const elapsed = performance.now() - (this.advanceStartedAt ?? performance.now())
    const remaining = Math.max(0, this.constructor.MIN_THINKING_MS - elapsed / this.pace)
    this.schedule(() => this.revealNextQuestion(), remaining)
  }

  revealNextQuestion() {
    if (!this.nextQuestion) return
    this.hideTyping()
    this.frameTarget?.classList.remove("an-hidden")
    this.currentQuestion = this.nextQuestion
    this.nextQuestion = null
    this.scrollToEnd()
  }

  // Back navigation: cancel any pending advance, drop the most recent answered
  // turn (question + answer), and restore the live question.
  popTurn() {
    this.clearPendingTimers()
    this.nextQuestion = null
    this.hideTyping()
    this.frameTarget?.classList.remove("an-hidden")
    if (!this.hasHistoryTarget) return
    for (let i = 0; i < 2 && this.historyTarget.lastElementChild; i++) {
      this.historyTarget.lastElementChild.remove()
    }
  }

  showTyping() {
    if (!this.hasHistoryTarget || this.typingEl) return
    const el = document.createElement("div")
    el.className = "an-typing"
    el.setAttribute("aria-label", "Anna schreibt …")
    el.innerHTML = "<span></span><span></span><span></span>"
    this.historyTarget.appendChild(el)
    this.typingEl = el
    this.scrollToEnd()
  }

  hideTyping() {
    this.typingEl?.remove()
    this.typingEl = null
  }

  appendBubble(text, who) {
    if (!this.hasHistoryTarget) return
    const el = document.createElement("div")
    el.className = `an-bubble an-bubble-${who}`
    el.textContent = text
    this.historyTarget.appendChild(el)
  }

  // Keep the newest message in view as the conversation grows. System specs
  // jump there at once, in the same task that shows the new question: reply
  // chips still gliding up under a smooth scroll make a spec's click miss.
  scrollToEnd() {
    if (isTestEnv()) {
      window.scrollTo({ top: document.body.scrollHeight, behavior: "instant" })
      return
    }
    requestAnimationFrame(() => {
      window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" })
    })
  }
}
